import { MappedSport, useSports } from '@sportninja/common/hooks/useNewSports'
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import { connect } from 'react-redux'
import { getSelfUser } from '@sportninja/common/selectors/users'
import {
  trackOnboardingContinue,
  trackOnboardingFinish,
} from 'src/utils/analytics'
import {
  saveWizardState,
  loadWizardState,
  clearWizardState,
  cleanupExpiredWizardStates,
  CompetitionFormData,
} from '../utils/localStorage'

export type LeagueType = 'simple' | 'complex' | null

export interface SportEntry {
  id: string
  sport_id: string
  generic_sport_id: string | null
  leagueName: string
  is_generic: boolean
}

export interface OnboardingWizardState {
  currentStep: number
  leagueType: LeagueType
  sportsData: SportEntry[]
  competitionData: any
}

interface OnboardingWizardContextType {
  state: OnboardingWizardState
  setLeagueType: (type: LeagueType) => void
  setSportsData: (sports: SportEntry[]) => void
  nextStep: () => void
  previousStep: () => void
  skipWizard: () => void
  closeWizard: () => void
  completeWizard: (scheduleId?: string) => void
  contactSpecialist: () => void
  sports: MappedSport[]
  sportsData: SportEntry[]
  genericSportId: string | null
  competitionFormData: CompetitionFormData | null
  setCompetitionFormData: (data: CompetitionFormData) => void
}

const OnboardingWizardContext = createContext<
  OnboardingWizardContextType | undefined
>(undefined)

interface OnboardingWizardProviderProps {
  children: ReactNode
  onClose: () => void
  onSkip?: () => void
  onComplete?: () => void
  genericSports: any[]
  sports: any[]
  me?: any
}

// Create a connected version for the Provider to access Redux state
interface ConnectedOnboardingWizardProviderProps
  extends OnboardingWizardProviderProps {
  me: any
}

const OnboardingWizardProviderComponent: React.FC<
  ConnectedOnboardingWizardProviderProps
> = ({ children, onClose, onSkip, onComplete, genericSports, sports, me }) => {
  const { sports: mappedSports } = useSports(sports, genericSports)
  const genericSportId = sports?.find(
    (sport) => sport.name === 'generic' || sport.name_full === 'Generic'
  )?.id
  const history = useHistory()
  const userId = me?.id

  // Helper function to get step name for analytics
  const getStepName = (stepNumber: number): string => {
    switch (stepNumber) {
      case 0:
        return 'get_started'
      case 1:
        return 'league_layout'
      case 2:
        return 'sports_setup'
      case 3:
        return 'first_competition'
      default:
        return 'unknown'
    }
  }

  const [state, setState] = useState<OnboardingWizardState>({
    currentStep: 0, // 0 = welcome, 1 = league setup, 2 = sports, 3 = competition
    leagueType: null,
    sportsData: [],
    competitionData: null,
  })

  const [competitionFormData, setCompetitionFormData] =
    useState<CompetitionFormData | null>(null)

  // Load saved state on mount and cleanup expired data
  useEffect(() => {
    if (userId) {
      console.log(
        '🔄 OnboardingWizardContext: Loading saved state for user',
        userId
      )

      // Run cleanup of expired wizard states
      cleanupExpiredWizardStates()

      const savedData = loadWizardState(userId)
      if (savedData) {
        console.log(
          '✅ OnboardingWizardContext: Restoring saved state',
          savedData
        )
        
        // Track that user is continuing from saved state
        const stepName = getStepName(savedData.state.currentStep)
        trackOnboardingContinue(stepName)
        
        setState(savedData.state)
        if (savedData.competitionFormData) {
          setCompetitionFormData(savedData.competitionFormData)
        }
      }
    }
  }, [userId])

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (userId && state.currentStep > 0) {
      // Don't save welcome step
      console.log('🔄 OnboardingWizardContext: Saving state to localStorage')
      saveWizardState(userId, state, competitionFormData || undefined)
    }
  }, [state, competitionFormData, userId])

  const setLeagueType = (type: LeagueType) => {
    setState((prev) => ({ ...prev, leagueType: type }))
  }

  const setSportsData = (sports: SportEntry[]) => {
    setState((prev) => ({ ...prev, sportsData: sports }))
  }

  const nextStep = () => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 3),
    }))
  }

  const previousStep = () => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }))
  }

  const skipWizard = async () => {
    console.log('🔄 OnboardingWizardContext: Skipping wizard')
    try {
      // We don't want to clear the state when skipping the wizard
      // if (userId) {
      //   clearWizardState(userId)
      // }
      if (onSkip) {
        await onSkip()
      }
      onClose()
    } catch (error) {
      console.error('🚨 OnboardingWizardContext: Error skipping wizard:', error)
      onClose()
    }
  }

  const closeWizard = () => {
    onClose()
  }

  const completeWizard = async (scheduleId?: string) => {
    console.log('🔄 OnboardingWizardContext: Completing wizard', { scheduleId })
    try {
      // Track wizard completion
      trackOnboardingFinish()
      
      // Clear saved state when successfully completing wizard
      if (userId) {
        clearWizardState(userId)
      }
      if (onComplete) {
        await onComplete()
      }
      // Redirect to the competition setup page with dynamic schedule ID
      // Add query parameter to indicate this is a new competition from onboarding
      if (scheduleId) {
        history.push(
          `${ROUTES.SCHEDULE_ROOT}/${scheduleId}/setup?new=true&fromOnboarding=true`
        )
      }
      onClose()
    } catch (error) {
      console.error(
        '🚨 OnboardingWizardContext: Error completing wizard:',
        error
      )
      onClose()
    }
  }

  const contactSpecialist = async () => {
    console.log('🔄 OnboardingWizardContext: User contacting specialist')
    try {
      // Clear saved state when user contacts specialist
      if (userId) {
        clearWizardState(userId)
      }
      // Mark as completed when user contacts specialist
      if (onComplete) {
        await onComplete()
      }
      // Open the booking link
      window.open(
        'https://info.sportninja.com/meetings/james-goodfellow/onboarding-and-product-qa',
        '_blank'
      )
      onClose()
    } catch (error) {
      console.error(
        '🚨 OnboardingWizardContext: Error contacting specialist:',
        error
      )
      onClose()
    }
  }

  const value: OnboardingWizardContextType = {
    state,
    setLeagueType,
    setSportsData,
    nextStep,
    previousStep,
    skipWizard,
    closeWizard,
    completeWizard,
    contactSpecialist,
    sports: mappedSports,
    sportsData: state.sportsData,
    genericSportId,
    competitionFormData,
    setCompetitionFormData,
  }

  return (
    <OnboardingWizardContext.Provider value={value}>
      {children}
    </OnboardingWizardContext.Provider>
  )
}

// Create a connected version that maps Redux state to props
const mapStateToProps = (state: any) => ({
  me: getSelfUser(state),
})

// Export the connected provider
export const OnboardingWizardProvider = connect(mapStateToProps)(
  OnboardingWizardProviderComponent
)

export const useOnboardingWizard = () => {
  const context = useContext(OnboardingWizardContext)
  if (!context) {
    throw new Error(
      'useOnboardingWizard must be used within OnboardingWizardProvider'
    )
  }
  return context
}
